import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaCommPreferenceV1Service, serviceName } from '../service';
import { ListByPreferenceIO, ServiceQueries } from '../types';
import { useCommPreferenceV1Queries } from './use-comm-preference-v1-queries';
import { UseCommPreferenceV1QueryEndpointArgs, useCommPreferenceV1Query } from './use-comm-preference-v1-query';

/**
 * A hook that returns a query for the `ListByPreference` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListByPreferenceQuery = <E = unknown, D = ListByPreferenceIO['output']>(
  args: UseCommPreferenceV1QueryEndpointArgs<'ListByPreference', E, D>
) =>
  useCommPreferenceV1Query({
    endpointName: 'ListByPreference',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `ListByPreference` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListByPreferenceQueries = <E = unknown, D = ListByPreferenceIO['output']>(
  argsArr: UseCommPreferenceV1QueryEndpointArgs<'ListByPreference', E, D>[]
) =>
  useCommPreferenceV1Queries(
    argsArr.map((args) => ({
      endpointName: 'ListByPreference',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `ListByPreference` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListByPreference = () => {
  const queryClient = useQueryClient();

  const fetchListByPreference = async <E = unknown, D = ListByPreferenceIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseCommPreferenceV1QueryEndpointArgs<'ListByPreference', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      ListByPreferenceIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListByPreference'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        ListByPreferenceIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListByPreference'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListByPreference'>({
          serviceName,
          endpointName: 'ListByPreference',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaCommPreferenceV1Service.ListByPreference(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchListByPreference;
};
